import { AxiosInstance } from "axios";
import { ApiRouteHelpers as Helpers } from "../../../root/apiRoutes";
import { CustomMessageSampleDto } from "../dto/CustomMessageSampleDto";
import { CustomMessageTypeDto } from "../dto/CustomMessageTypeDto";
import { OkReturnDto } from "../dto/OkReturnDto";
import { OptionalDto } from "../dto/OptionalDto";
import { OrganizationComplianceChecklistItemDto } from "../dto/OrganizationComplianceChecklistItemDto";
import {
  OrganizationDto,
  OrganizationPostmarkMailboxDto,
  OrganizationPostmarkMailboxShareDto,
  OrganizationPostmarkMailboxShareOptionDto,
  PostmarkTestSendResponse,
} from "../dto/OrganizationDto";
import { OrganizationFeatureDto } from "../dto/OrganizationFeatureDto";
import { OrganizationMailboxDto } from "../dto/OrganizationMailboxDto";
import { OrganizationPublicId, SendMethod, UUIDString } from "../Types";
import { RegulationRequestTypeDto } from "../dto/RegulationRequestTypeDto";
import { OrganizationJwtDto } from "../dto/OrganizationJwtDto";
import { EventPulseLaunchDto } from "../dto/EventPulseLaunchDto";
import { CreatedMcpTokenDto, McpTokenDto } from "../dto/McpTokenDto";
import { TruevaultOnboardingProgress } from "../../../hooks/useOnboardingProgress";

export type UpdateOrganization = {
  name: string;
  logoUrl: string | null;
  regulations: UpdateRegulations[];
  autoSendRequestEmailNotificationsForDeleteRequests?: boolean;
  autoSendRequestEmailNotificationsForOptOutRequests?: boolean;
  autoSendRequestEmailNotificationsForAccessRequests?: boolean;
  autoSendRequestEmailNotificationsForLimitRequests?: boolean;
  primaryLookupSystemVendorId?: string;
  secondaryLookupSystemVendorId?: string;
  finishedOnboardingLatch?: boolean;
};

export type UpdateOrganizationGoogleAnalyticsWebPropertyDto = {
  webProperty: string;
};

export type UpdateMessageSettings = {
  headerImageUrl: string;
  physicalAddress: string;
  messageSignature: string;
};

export type UpdateOrganizationPostmarkMailbox = {
  mailbox: string;
};

export type UpdateOrganizationPostmarkMailboxShare = {
  ownerOrganizationId: OrganizationPublicId;
};

export type UpdateOrganizationSendMethod = {
  sendMethod: SendMethod;
};

export type PostmarkTestSendRequest = {
  fromEmail: string;
  toEmail: string;
  subject?: string;
  textBody?: string;
  htmlBody?: string;
  messageStream?: string;
};

export type UpdateCustomField =
  | { customFieldEnabled: false }
  | {
      customFieldEnabled: true;
      customFieldLabel: string;
      customFieldHelp: string;
    };

export type UpdateFinancialIncentives = { financialIncentiveDetails: string };

export type UpdateRegulations = {
  id: UUIDString;
  enabled: boolean;
};

export type UpdateVerificationInstruction = {
  regulationSlug: string;
  instruction: string;
};

export type UpdateOrganizationComplianceChecklistItemDto = {
  slug: string;
  description?: string;
  done?: boolean;
};

export type CreateOrganizationJwt = {
  expiryInSeconds: number;
};

export class OrganizationController {
  constructor(private axios: AxiosInstance) {}

  public getOrganization = async (organizationId) => {
    const url = Helpers.organization.root(organizationId);
    const response = await this.axios.get<OrganizationDto>(url, {});

    return response.data as OrganizationDto;
  };

  public updateOrganization = async (
    organizationId: OrganizationPublicId,
    updatedOrganization: UpdateOrganization,
  ) => {
    const url = Helpers.organization.root(organizationId);
    const response = await this.axios.put<OrganizationDto>(url, updatedOrganization);

    return response.data as OrganizationDto;
  };

  public updateSendMethod = async (
    organizationId: OrganizationPublicId,
    payload: UpdateOrganizationSendMethod,
  ) => {
    const url = Helpers.organization.sendMethod(organizationId);
    const response = await this.axios.patch<OrganizationDto>(url, payload);

    return response.data as OrganizationDto;
  };

  public updateGaWebProperty = async (
    organizationId: OrganizationPublicId,
    update: UpdateOrganizationGoogleAnalyticsWebPropertyDto,
  ) => {
    const url = Helpers.organization.gaWebProperty(organizationId);
    const response = await this.axios.put<OrganizationDto>(url, update);
    return response.data as OrganizationDto;
  };

  public getOrganizationMailboxes = async (organizationId) => {
    const url = Helpers.organization.mailboxes(organizationId);
    const response = await this.axios.get<OrganizationMailboxDto[]>(url);

    return response.data as OrganizationMailboxDto[];
  };

  public getOrganizationPostmarkMailbox = async (organizationId: OrganizationPublicId) => {
    const url = Helpers.organization.postmarkMailbox(organizationId);
    const response = await this.axios.get<OptionalDto<OrganizationPostmarkMailboxDto>>(url);
    return response.data.value;
  };

  public getOrganizationPostmarkMailboxShare = async (organizationId: OrganizationPublicId) => {
    const url = Helpers.organization.postmarkMailboxShare(organizationId);
    const response = await this.axios.get<OptionalDto<OrganizationPostmarkMailboxShareDto>>(url);
    return response.data.value;
  };

  public getOrganizationPostmarkMailboxShareOptions = async (
    organizationId: OrganizationPublicId,
  ) => {
    const url = Helpers.organization.postmarkMailboxShareOptions(organizationId);
    const response = await this.axios.get<OrganizationPostmarkMailboxShareOptionDto[]>(url);
    return response.data as OrganizationPostmarkMailboxShareOptionDto[];
  };

  public upsertOrganizationPostmarkMailboxShare = async (
    organizationId: OrganizationPublicId,
    payload: UpdateOrganizationPostmarkMailboxShare,
  ) => {
    const url = Helpers.organization.postmarkMailboxShare(organizationId);
    const response = await this.axios.put<OrganizationPostmarkMailboxShareDto>(url, payload);
    return response.data as OrganizationPostmarkMailboxShareDto;
  };

  public deleteOrganizationPostmarkMailboxShare = async (organizationId: OrganizationPublicId) => {
    const url = Helpers.organization.postmarkMailboxShare(organizationId);
    await this.axios.delete(url);
  };

  public upsertOrganizationPostmarkMailbox = async (
    organizationId: OrganizationPublicId,
    payload: UpdateOrganizationPostmarkMailbox,
  ) => {
    const url = Helpers.organization.postmarkMailbox(organizationId);
    const response = await this.axios.put<OrganizationPostmarkMailboxDto>(url, payload);
    return response.data as OrganizationPostmarkMailboxDto;
  };

  public verifyOrganizationPostmarkMailbox = async (organizationId: OrganizationPublicId) => {
    const url = Helpers.organization.postmarkMailboxVerify(organizationId);
    const response = await this.axios.post<OrganizationPostmarkMailboxDto>(url);
    return response.data as OrganizationPostmarkMailboxDto;
  };

  public sendPostmarkTestEmail = async (
    organizationId: OrganizationPublicId,
    payload: PostmarkTestSendRequest,
  ) => {
    const url = Helpers.organization.postmarkMailboxTest(organizationId);
    const response = await this.axios.post<PostmarkTestSendResponse>(url, payload);
    return response.data as PostmarkTestSendResponse;
  };

  public getDataRequestTypes = async (
    organizationId: OrganizationPublicId,
    params?: { filterVisible?: boolean; regulations?: string },
  ): Promise<RegulationRequestTypeDto[]> => {
    const url = Helpers.organization.dataRequestTypes(organizationId);
    const response = await this.axios.get<RegulationRequestTypeDto[]>(url, { params });
    return response.data;
  };

  public getOrganizationOnboardingProgress = async (organizationId) => {
    const url = Helpers.organization.onboardingProgress(organizationId);
    const response = await this.axios.get<TruevaultOnboardingProgress>(url);

    return response.data as TruevaultOnboardingProgress;
  };

  public getOrganizationFeatures = async (organizationId) => {
    const url = Helpers.organization.features(organizationId);
    const response = await this.axios.get<OrganizationFeatureDto[]>(url);

    return response.data as OrganizationFeatureDto[];
  };

  public sendOrganizationSurvey = async (
    organizationId: OrganizationPublicId,
    surveyName: string,
  ): Promise<OkReturnDto> => {
    const url = Helpers.organization.surveySend(organizationId, surveyName);
    const response = await this.axios.post<OkReturnDto>(url);
    return response.data as OkReturnDto;
  };

  public getOrganizationComplianceChecklist = async (
    organizationId: OrganizationPublicId,
  ): Promise<OrganizationComplianceChecklistItemDto[]> => {
    const url = Helpers.organization.complianceChecklist(organizationId);
    const response = await this.axios.get<OrganizationComplianceChecklistItemDto[]>(url);

    return response.data;
  };

  public async getOrganizationMessageTypes(
    organizationId: OrganizationPublicId,
  ): Promise<CustomMessageTypeDto[]> {
    const url = Helpers.organization.customMessageType.list(organizationId);
    const response = await this.axios.get<CustomMessageTypeDto[]>(url);

    return response.data;
  }

  public async getOrganizationMessageSample(
    organizationId: OrganizationPublicId,
    slug: string,
  ): Promise<CustomMessageSampleDto> {
    const url = Helpers.organization.customMessageType.specific(organizationId, slug);
    const response = await this.axios.get<CustomMessageSampleDto>(url);

    return response.data;
  }

  public async updateOrganizationMessageSample(
    organizationId: OrganizationPublicId,
    slug: string,
    text: string,
  ): Promise<OkReturnDto> {
    const url = Helpers.organization.customMessageType.specific(organizationId, slug);
    const response = await this.axios.put<OkReturnDto>(url, { customText: text });

    return response.data;
  }

  public updateOrganizationComplianceChecklist = async (
    organizationId: OrganizationPublicId,
    updateQuestionsDto: UpdateOrganizationComplianceChecklistItemDto[],
  ): Promise<OkReturnDto> => {
    const url = Helpers.organization.complianceChecklist(organizationId);
    const response = await this.axios.post<OkReturnDto>(url, updateQuestionsDto);
    return response.data as OkReturnDto;
  };

  public updateMessageSettings = async (
    organizationId: OrganizationPublicId,
    updatedMessageSettings: UpdateMessageSettings,
  ) => {
    const url = Helpers.organization.messageSettings(organizationId);
    const response = await this.axios.put<OrganizationDto>(url, updatedMessageSettings);

    return response.data as OrganizationDto;
  };

  public updateCustomField = async (
    organizationId: OrganizationPublicId,
    updatedMessageSettings: UpdateCustomField,
  ) => {
    const url = Helpers.organization.customField(organizationId);
    const response = await this.axios.put<OrganizationDto>(url, updatedMessageSettings);

    return response.data as OrganizationDto;
  };

  public updateCustomOptOutField = async (
    organizationId: OrganizationPublicId,
    updatedMessageSettings: UpdateCustomField,
  ) => {
    const url = Helpers.organization.customOptOutField(organizationId);
    const response = await this.axios.put<OrganizationDto>(url, updatedMessageSettings);

    return response.data as OrganizationDto;
  };

  public updateFinancialIncentives = async (
    organizationId: OrganizationPublicId,
    updatedIncentive: string,
  ) => {
    const url = Helpers.organization.financialIncentives(organizationId);
    const response = await this.axios.put<OrganizationDto>(url, {
      financialIncentiveDetails: updatedIncentive,
    } as UpdateFinancialIncentives);

    return response.data as OrganizationDto;
  };

  public updateVerificationInstruction = async (
    organizationId: OrganizationPublicId,
    updateVerificationInstruction: UpdateVerificationInstruction,
  ) => {
    const url = Helpers.organization.verificationInstruction(organizationId);
    const response = await this.axios.put<OrganizationDto>(url, updateVerificationInstruction);

    return response.data as OrganizationDto;
  };

  public setGetCompliantDone = async (
    organizationId: OrganizationPublicId,
    force: boolean,
  ): Promise<boolean> => {
    const url = Helpers.organization.getCompliantDone(organizationId);
    const params = { force };
    const response = await this.axios.post<boolean>(url, null, { params });
    return response.data as boolean;
  };

  public testMessage = async (organizationId: OrganizationPublicId) => {
    const url = Helpers.organization.testMessage(organizationId);
    const response = await this.axios.post(url);

    return response.data;
  };

  public deleteOrganizationMailbox = async (organizationId, mailbox) => {
    const url = Helpers.organization.mailbox(organizationId, mailbox);
    const response = await this.axios.delete(url, {});

    return response.data;
  };

  public createCustomPic = async (organizationId: OrganizationPublicId, name: string) => {
    const url = Helpers.organization.categories.root(organizationId);
    const response = await this.axios.post(url, { name });

    return response.data;
  };

  public deleteCustomPic = async (organizationId: OrganizationPublicId, picId: string) => {
    const url = Helpers.organization.categories.specific(organizationId, picId);
    const response = await this.axios.delete(url, {});

    return response.data;
  };

  public createOrgJwt = async (
    organizationId: OrganizationPublicId,
    organizationJwtSettings?: CreateOrganizationJwt,
  ) => {
    const url = Helpers.organization.jwt(organizationId);
    const response = await this.axios.post(url, organizationJwtSettings);

    return response.data as OrganizationJwtDto;
  };

  public getEventPulseLaunchUrl = async (
    organizationId: OrganizationPublicId,
  ): Promise<EventPulseLaunchDto> => {
    const url = Helpers.organization.eventPulseLaunch(organizationId);
    const response = await this.axios.get<EventPulseLaunchDto>(url);
    return response.data;
  };

  public listMcpTokens = async (organizationId: OrganizationPublicId): Promise<McpTokenDto[]> => {
    const url = Helpers.organization.mcpTokens.root(organizationId);
    const response = await this.axios.get<McpTokenDto[]>(url);
    return response.data;
  };

  /** The response carries the plaintext token. It cannot be read back afterwards. */
  public createMcpToken = async (
    organizationId: OrganizationPublicId,
  ): Promise<CreatedMcpTokenDto> => {
    const url = Helpers.organization.mcpTokens.root(organizationId);
    const response = await this.axios.post<CreatedMcpTokenDto>(url);
    return response.data;
  };

  /** Resets expiry to 30 days from now, whatever it was before. */
  public extendMcpToken = async (
    organizationId: OrganizationPublicId,
    tokenId: string,
  ): Promise<McpTokenDto> => {
    const url = Helpers.organization.mcpTokens.extend(organizationId, tokenId);
    const response = await this.axios.post<McpTokenDto>(url);
    return response.data;
  };

  public revokeMcpToken = async (
    organizationId: OrganizationPublicId,
    tokenId: string,
  ): Promise<McpTokenDto> => {
    const url = Helpers.organization.mcpTokens.specific(organizationId, tokenId);
    const response = await this.axios.delete<McpTokenDto>(url);
    return response.data;
  };
}
